import { type FormEvent, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { useAuth } from '../lib/auth-context.js';
import { ApiError } from '../lib/api.js';

const { t } = i18n;

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await auth.login(email, password, rememberMe);
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_credentials') {
        setError(t('auth.login.invalid_credentials'));
      } else if (err instanceof ApiError && err.code === 'rate_limited') {
        setError(t('auth.login.rate_limited'));
      } else {
        setError(t('auth.login.network_error'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="w-full max-w-sm space-y-4 rounded-lg border border-border p-6"
        aria-busy={busy}
      >
        <h1 className="text-lg font-semibold">{t('auth.login.title')}</h1>

        <label className="block space-y-1">
          <span className="text-sm">{t('auth.login.email')}</span>
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
          />
        </label>

        <label className="block space-y-1">
          <span className="text-sm">{t('auth.login.password')}</span>
          <input
            type="password"
            autoComplete="current-password"
            required
            minLength={1}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5 text-sm"
          />
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="size-4 accent-primary"
          />
          <span>{t('auth.login.remember_me')}</span>
        </label>

        {error ? (
          <div role="alert" className="text-sm text-red-600">
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={busy || !email || !password}
          className="w-full rounded bg-primary text-primary-foreground py-1.5 text-sm font-medium disabled:opacity-50"
        >
          {busy ? t('common.loading') : t('auth.login.submit')}
        </button>

        <a
          href="/password-reset"
          className="block text-center text-sm font-medium text-primary hover:underline"
        >
          {t('auth.login.forgot')}
        </a>
      </form>
    </div>
  );
}
